import React, { useEffect, useState, useCallback } from "react";
import { Users, Shield, RefreshCw, AlertCircle, UserPlus, Mail, Clock } from "lucide-react";
import { roleApi } from "../services/api";
import { formatDate } from "../utils/dateFormatter";

const RolesAdmin = () => {
  const [roles, setRoles] = useState([]);
  const [totals, setTotals] = useState({ roles: 0, users: 0, lastUpdated: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRoleDirectory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await roleApi.getDirectory();

      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to load role directory");
      }

      setRoles(response.data.roles || []);
      setTotals(response.data.totals || { roles: 0, users: 0, lastUpdated: "" });
    } catch (err) {
      console.error("Role directory fetch error:", err);
      const message = err instanceof Error ? err.message : "Unable to load roles";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoleDirectory();
  }, [fetchRoleDirectory]);

  // Date formatting now handled by utils/dateFormatter with IST timezone

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to load roles</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchRoleDirectory}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Roles</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{totals.roles}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Shield className="text-blue-600" size={20} />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Active Admins</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{totals.users}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <Users className="text-emerald-600" size={20} />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Last Updated</p>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {totals.lastUpdated ? formatDate(totals.lastUpdated) : "Just now"}
            </p>
          </div>
          <button
            onClick={fetchRoleDirectory}
            className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw size={16} className="text-gray-600" />
            Refresh
          </button>
        </div>
      </div>

      {/* Roles list */}
      <div className="space-y-3">
        {roles.map((role) => (
          <div key={role.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {role.description || "No description available"}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 w-fit">
                <Users size={16} />
                {role.userCount} {role.userCount === 1 ? "user" : "users"}
              </span>
            </div>

            <div className="mt-4 space-y-2">
              {role.users.length === 0 ? (
                <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg p-3">
                  No administrators assigned to this role yet.
                </div>
              ) : (
                role.users.map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border border-gray-100 rounded-lg p-3 bg-gray-50"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{user.name || "Unnamed admin"}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-0.5">
                        <Mail size={14} />
                        <span>{user.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock size={14} />
                      <span>Added {formatDate(user.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RolesAdmin;
